import React from 'react';
import { getIntentTone } from './intentTone';
import { formatByHint } from './criteriaBarUtils';

export default function WindowComparisonCard({ windowComparison, intent, currency = 'USD', title, subtitle }) {
  if (!windowComparison || typeof windowComparison !== 'object') return null;
  const windows = Array.isArray(windowComparison.windows) ? windowComparison.windows : [];
  if (windows.length < 2) return null;

  const tone = getIntentTone(intent);
  const metricKey = String(windowComparison?.metric_key || '').toLowerCase();
  const metricLabel = String(windowComparison?.metric_label || windowComparison?.metric_key || 'Métrica');
  const alertLabel = String(windowComparison?.alert_window_label || windows[0]?.label || 'Ventana corta');
  const baselineLabel = String(windowComparison?.baseline_window_label || windows[1]?.label || 'Ventana base');

  const a = windows[0]?.value;
  const b = windows[1]?.value;
  const aNum = typeof a === 'number' ? a : Number(a);
  const bNum = typeof b === 'number' ? b : Number(b);
  // Format strictly by the entry's format_hint — never by metric name or magnitude.
  const fmt = (n) => formatByHint(n, windowComparison?.format_hint, currency);

  const deltaRatio =
    Number.isFinite(aNum) && Number.isFinite(bNum) && bNum !== 0 ? (aNum - bNum) / bNum : null;
  const deltaText =
    typeof deltaRatio === 'number'
      ? `${deltaRatio >= 0 ? '+' : '−'}${Math.abs(deltaRatio * 100).toFixed(0)}% vs base`
      : null;

  const shortWindowCpaWorse =
    metricKey.includes('cpa') &&
    Number.isFinite(aNum) &&
    Number.isFinite(bNum) &&
    bNum > 0 &&
    aNum > bNum;

  const severity = String(windowComparison?.alert_severity || '').toLowerCase();
  const severityTone =
    severity === 'high'
      ? { bg: 'rgba(245,158,11,0.18)', border: 'rgba(245,158,11,0.30)', text: 'rgba(255,251,235,0.95)' }
      : { bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.10)', text: 'rgba(255,255,255,0.85)' };

  return (
    <div
      className="mt-3 rounded-xl border bg-white/5 px-4 py-3"
      style={{ borderColor: severityTone.border }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs tracking-wide text-white/60 font-semibold">
            {title || 'Cambio entre ventanas'}
          </div>
          <div className="mt-0.5 text-[11px] text-white/55">
            {subtitle != null && String(subtitle).trim() !== '' ? subtitle : metricLabel}
          </div>
        </div>
        {deltaText ? (
          <span
            className="shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap"
            style={{
              background: severityTone.bg,
              borderColor: severityTone.border,
              color: severityTone.text,
            }}
          >
            {deltaText}
          </span>
        ) : null}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-white/45 truncate">
            {alertLabel}
          </div>
          <div
            className={`mt-1 text-lg font-semibold tabular-nums ${shortWindowCpaWorse ? 'text-rose-200' : ''}`}
            style={shortWindowCpaWorse ? undefined : { color: tone.surface }}
          >
            {fmt(aNum)}
          </div>
        </div>
        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
          <div className="text-[10px] uppercase tracking-wider text-white/45 truncate">
            {baselineLabel}
          </div>
          <div className="mt-1 text-lg font-semibold tabular-nums text-white">
            {fmt(bNum)}
          </div>
        </div>
      </div>
    </div>
  );
}
